import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface MenuItem {
  title: string;
  link?: string;
  disabled?: boolean;
}

interface MenuColumn {
  label: string;
  items: MenuItem[];
}

const solutionsColumns: MenuColumn[] = [
  {
    label: 'Services',
    items: [
      { title: 'Photogrammetry', link: '/photogrammetry' },
      { title: 'Thermal Imaging', link: '/thermal-imaging' },
      { title: 'LiDAR Mapping', link: '/lidar-mapping' },
      { title: 'Multispectral Imaging', link: '/multispectral-imaging' },
    ],
  },
  {
    label: 'Products',
    items: [
      { title: 'Coming Soon', disabled: true },
    ],
  },
];

const industriesColumns: MenuColumn[] = [
  {
    label: 'Energy & Resources',
    items: [
      { title: 'Renewable Energy', link: '/industries/energy-resources#renewable-energy' },
      { title: 'Oil & Gas', link: '/industries/energy-resources#oil-gas' },
      { title: 'Mining & Resources', link: '/industries/energy-resources#mining-resources' },
      { title: 'Power & Utilities', link: '/industries/energy-resources#power-utilities' },
    ],
  },
 {
  label: 'Infrastructure & Development',
  items: [
    { title: 'Construction & Real Estate', link: '/industries/infrastructure-development#construction-real-estate' },
    { title: 'Telecommunications', link: '/industries/infrastructure-development#telecommunications' },
    { title: 'Transportation', link: '/industries/infrastructure-development#transportation' },
    { title: 'Agriculture & Forestry', link: '/industries/infrastructure-development#agriculture-forestry' },
  ],
},
];

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  activeMenu: string | null;
}

function MenuItemRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  if (item.disabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 0,
        }}
      >
        <Box sx={{ width: '12px', height: '1px', bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Typography
          sx={{
            fontSize: '13px',
            fontFamily: 'Jost, sans-serif',
            fontStyle: 'italic',
            color: 'rgba(255,255,255,0.25)',
          }}
        >
          {item.title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component={RouterLink}
      to={item.link!}
      onClick={onClose}
      sx={{
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 0,
        transition: 'all 0.3s ease',
        '&:hover .menu-text': { color: '#E80029' },
        '&:hover .menu-dash': { bgcolor: '#E80029', width: '18px' },
        '&:hover .menu-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      <Box
        className="menu-dash"
        sx={{
          width: '12px',
          height: '1px',
          bgcolor: 'rgba(255,255,255,0.15)',
          transition: 'all 0.3s ease',
        }}
      />
      <Typography
        className="menu-text"
        sx={{
          fontSize: '13px',
          letterSpacing: '0.04em',
          fontFamily: 'Jost, sans-serif',
          fontWeight: 500,
          color: 'rgba(255,255,255,0.65)',
          transition: 'color 0.3s',
        }}
      >
        {item.title}
      </Typography>
      <ArrowRight
        className="menu-arrow"
        size={13}
        color="#E80029"
        style={{
          opacity: 0,
          transform: 'translateX(-6px)',
          transition: 'all 0.3s ease',
        }}
      />
    </Box>
  );
}

export default function MegaMenu({ isOpen, onClose, onMouseEnter, onMouseLeave, activeMenu }: MegaMenuProps) {
  const columns = activeMenu === 'Solutions' ? solutionsColumns : industriesColumns;
  const viewAllLink = activeMenu === 'Solutions' ? '/solutions' : '/industries';
  const viewAllLabel = activeMenu === 'Solutions' ? 'View All Solutions' : 'Explore All Industries';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          style={{
            position: 'fixed',
            top: 88,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 1199,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <Box
              sx={{
                maxWidth: '1920px',
                mx: 'auto',
                px: { xs: 3, md: 6 },
                py: 5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0,
              }}
            >
              {columns.map((col, idx) => (
                <React.Fragment key={col.label}>
                  {/* Vertical divider between columns */}
                  {idx > 0 && (
                    <Box
                      sx={{
                        width: '1px',
                        alignSelf: 'stretch',
                        mx: 5,
                        background:
                          'linear-gradient(180deg, rgba(232,0,41,0.3), rgba(255,255,255,0.05), transparent)',
                      }}
                    />
                  )}

                  <Box sx={{ flex: 1 }}>
                    {/* Column label */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          bgcolor: '#E80029',
                          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '10px',
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 'bold',
                          color: 'rgba(255,255,255,0.35)',
                        }}
                      >
                        {col.label}
                      </Typography>
                    </Box>

                    {/* Column items */}
                    {col.items.map((item) => (
                      <MenuItemRow key={item.title} item={item} onClose={onClose} />
                    ))}
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            {/* Bottom bar — View all link */}
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Box
                component={RouterLink}
                to={viewAllLink}
                onClick={onClose}
                sx={{
                  maxWidth: '1920px',
                  mx: 'auto',
                  px: { xs: 3, md: 6 },
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  textDecoration: 'none',
                  transition: 'background 0.3s',
                  '&:hover': { bgcolor: 'rgba(232,0,41,0.04)' },
                  '&:hover .footer-text': { color: '#E80029' },
                  '&:hover .footer-arrow': { transform: 'translateX(4px)' },
                }}
              >
                <Typography
                  className="footer-text"
                  sx={{
                    fontSize: '11px',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 'bold',
                    color: 'rgba(255,255,255,0.35)',
                    transition: 'color 0.3s',
                  }}
                >
                  {viewAllLabel}
                </Typography>
                <ArrowRight
                  className="footer-arrow"
                  size={14}
                  color="rgba(255,255,255,0.3)"
                  style={{ transition: 'transform 0.3s' }}
                />
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}